import { Fragment, useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { postActivityLogs } from "../../redux/slices/activity-logs-slice";
import { DashboardCardPrimary, DashboardCardSecondary, LineChart, LoaderComponent, PieChart, TabsComponent } from "../../components";
import { HomeCardsMain, HomeCardsContainer, ChartsContainerMain } from "./dashboard.style";
import { AuthPageContainer } from "../../styles/global-styles";
import { dashboardTabsConstant, dashboardTabsOptions } from "../../constants/app-constants";
import { CheckFileSvg, CheckSvg, ChevronRightSvg, CrossSvg, UserClockSvg, WarningSvg } from "../../assets/svgs";

const Dashboard = () => {
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.loginUserReducer);
    const { loading, activities } = useSelector((state) => state.activityLogsReducer);

    // Map tabs to API periods
    const tabToPeriodMap = {
        [dashboardTabsConstant.today]: "daily",
        [dashboardTabsConstant.monthToDate]: "monthly",
        [dashboardTabsConstant.yearToDate]: "yearly"
    };

    const initialStates = {
        todayActivities: [],
        monthlyActivities: [],
        yearlyActivities: [],
        isModalOpen: false,
    };

    // State to hold activities data for each tab
    const [states, setStates] = useState(initialStates);
    // Default selected tab
    const [selectedTab, setSelectedTab] = useState(dashboardTabsConstant.today);

    // const initialForm = {
    //     startTime: "00:00:00",
    //     endTime: "23:59:59",
    //     institution: user?.superAdmin ? null : user?.institution?.value,
    // };

    // const [form,] = useState(initialForm);

    // const getActivitiesData = () => {
    //     if (user && form) {
    //         dispatch(postActivityLogs({ user, time: form }));
    //     }
    // };

    // const handleReload = () => {
    //     getActivitiesData();
    // };

    // const handleModalToggle = () => {
    //     setStates((old) => ({ ...old, isModalOpen: !states.isModalOpen }));
    // };

    // const handleChange = (e) => {
    //     const { name, value } = e.target;
    //     setForm((old) => ({ ...old, [name]: value }));
    // };

    // const handleSubmitFilter = (e) => {
    //     e.preventDefault();
    //     getActivitiesData();
    //     handleModalToggle();
    // };

    const getStatLabel = (tab) => {
        switch (tab) {
            case dashboardTabsConstant.today:
                return "from yesterday";
            case dashboardTabsConstant.monthToDate:
                return "from last month";
            case dashboardTabsConstant.yearToDate:
                return "from last year";
            default:
                return "";
        }
    };

    const getActivitiesData = useCallback(() => {
        if (user) {
            dispatch(postActivityLogs({ user }));
        }
    }, [dispatch, user]);

    const formattedStr = (str) => {
        if (!str) return '';
        switch (str.toLowerCase()) {
            case dashboardTabsConstant.monthToDate:
                return 'Month To Date';
            case dashboardTabsConstant.yearToDate:
                return 'Year To Date';
            case dashboardTabsConstant.today:
                return 'Today';
            default:
                return str
                    .replace(/([a-z])([A-Z])/g, '$1 $2')
                    .replace(/_/g, ' ')
                    .replace(/([a-z])([A-Z])/g, '$1 $2')
                    .toLowerCase()
                    .replace(/\b\w/g, (char) => char.toUpperCase());
        }
    };

    useEffect(() => {
        getActivitiesData();
    }, [dispatch, user, getActivitiesData]);


    useEffect(() => {
        if (!activities) return;

        const periods = {
            daily: 'todayActivities',
            monthly: 'monthlyActivities',
            yearly: 'yearlyActivities'
        };

        const getActivityData = (summary, period) => ([
            {
                label: 'Total Transactions',
                value: summary.total.current,
                statValue: summary.total.percentDifference,
                iconPrimary: CheckFileSvg,
                title: 'Frictionless Transactions',
                valuePrimary: summary.frictionlessFlow.total,
                valueSecondary: summary.frictionlessFlow.successRate,
                iconSecondary: ChevronRightSvg,

            },
            {
                label: 'Approved Transactions',
                value: summary.approved.current,
                statValue: summary.approved.percentDifference,
                iconPrimary: CheckSvg,
                title: 'Challenge Transactions',
                valuePrimary: summary.challengeFlow.total,
                valueSecondary: summary.challengeFlow?.successRate,
                iconSecondary: WarningSvg,

            },
            {
                label: 'Rejected Transactions',
                value: summary.rejected.current,
                statValue: summary.rejected.percentDifference,
                iconPrimary: CrossSvg,
                title: 'Pending Transactions',
                valuePrimary: summary.pending.current,
                // valueSecondary: summary.pending?.avgTime === 'N/A' || !summary.pending?.avgTime ? '0s' : summary.pending?.avgTime,
                valueSecondary: summary.pending?.avgTime,
                iconSecondary: UserClockSvg,
            },
        ]);

        const updatedStates = {};

        Object.entries(periods).forEach(([period, stateKey]) => {
            const periodData = activities.find(item => item.period === period);
            if (periodData) {
                updatedStates[stateKey] = getActivityData(periodData.transactionSummary, period);
            }
        });

        setStates(prev => ({ ...prev, ...updatedStates }));
    }, [activities]);


    const handleTabChange = (tab) => {
        setSelectedTab(tab);
    };

    const getChartData = (period) => {
        if (!activities) return [];

        const data = activities.find(item => item.period === tabToPeriodMap[period]);
        if (!data?.transactionDetailSummary?.transactionSummaries) return [];

        return data.transactionDetailSummary.transactionSummaries.map(item => {
            let date = item.unit;

            if (period === dashboardTabsConstant.monthToDate) {
                date = String(new Date(item.unit).getDate());
            } else if (period === dashboardTabsConstant.today) {
                date = item.unit.replace(":00", "").replace(" : ", "");
            }

            return {
                date,
                volume: item.total
            };
        });
    };

    const initialPieData = {
        backgroundColors: ['#d1d5db', '#9ca3af', '#6b7280'],
        data: [1, null, null],
        labels: ['Frictionless', 'Challenge', 'Rejected']
    };

    // Get pie chart data based on period
    const getPieData = (period) => {
        if (!activities) return initialPieData;

        const data = activities.find(item => item.period === tabToPeriodMap[period]);
        if (!data) return initialPieData;

        const frictionless = data.transactionSummary?.frictionlessFlow?.total ?? 0;
        const challenge = data.transactionSummary?.challengeFlow?.total ?? 0;
        const rejected = data.transactionSummary?.rejected?.current ?? 0;

        const isAllZero = frictionless === 0 && challenge === 0 && rejected === 0;

        return isAllZero
            ? initialPieData
            : {
                backgroundColors: ['#3989d8', '#397D4E', '#CF4C4A'],
                data: [
                    data.transactionSummary.frictionlessFlow.total,
                    data.transactionSummary.challengeFlow.total,
                    data.transactionSummary.rejected.current
                ],
                labels: ['Frictionless', 'Challenge', 'Rejected']
            };
    };

    const renderCards = (activities, type) => {
        const chartData = getChartData(type);
        const pieData = getPieData(type);

        return (
            <Fragment>
                <HomeCardsContainer>
                    {activities.map((item, index) => (
                        <DashboardCardPrimary
                            key={index}
                            label={item.label}
                            value={item.value}
                            statValue={item.statValue} // Could use percentDifference from API if needed
                            statLabel={getStatLabel(selectedTab)}
                            statsIcon={true}
                            iconPrimary={item.iconPrimary}
                            variant={
                                item.label.toLowerCase().includes('total') ? 'default' :
                                    item.label.toLowerCase().includes('approved') ? 'success' :
                                        'error'
                            }
                        />
                    ))}
                </HomeCardsContainer>

                <ChartsContainerMain>
                    <LineChart
                        chartTitle={`${formattedStr(type)} Transactions`}
                        data={chartData}
                        xAxisLabel={type === dashboardTabsConstant.today ? 'Hours of Day' : type === dashboardTabsConstant.monthToDate ? 'Days of Month' : 'Months of Year'}
                        pointRadius={2}
                    />

                    <PieChart
                        backgroundColors={pieData.backgroundColors}
                        data={pieData.data}
                        labels={pieData.labels}
                        legendPosition="right"
                        title="Transaction Flow"
                    />
                </ChartsContainerMain>

                <HomeCardsContainer>
                    {activities.map((item, index) => (
                        <DashboardCardSecondary
                            key={index}
                            title={item.title}
                            labelPrimary="Total Volume"
                            valuePrimary={item.valuePrimary}
                            valueSecondary={item.valueSecondary || 0}
                            iconSecondary={item.iconSecondary}
                            variant={
                                item.title.toLowerCase().includes('frictionless') ? 'default' :
                                    item.title.toLowerCase().includes('challenge') ? 'success' :
                                        'pending'
                            }
                        />
                    ))}
                </HomeCardsContainer>
            </Fragment>
        );
    };

    return (
        <AuthPageContainer>
            {
                loading &&
                <LoaderComponent />
            }

            {
                !loading &&
                <TabsComponent
                    tabs={dashboardTabsOptions}
                    value={selectedTab}
                    onChange={handleTabChange}
                >
                    {selectedTab === dashboardTabsConstant.today && (
                        <HomeCardsMain key={dashboardTabsConstant.today}>
                            {renderCards(states.todayActivities, dashboardTabsConstant.today)}
                        </HomeCardsMain>
                    )}

                    {selectedTab === dashboardTabsConstant.monthToDate && (
                        <HomeCardsMain key={dashboardTabsConstant.monthToDate}>
                            {renderCards(states.monthlyActivities, dashboardTabsConstant.monthToDate)}
                        </HomeCardsMain>
                    )}

                    {selectedTab === dashboardTabsConstant.yearToDate && (
                        <HomeCardsMain key={dashboardTabsConstant.yearToDate}>
                            {renderCards(states.yearlyActivities, dashboardTabsConstant.yearToDate)}
                        </HomeCardsMain>
                    )}
                </TabsComponent>
            }
        </AuthPageContainer>
    );
};

export default Dashboard;